import { Text } from "@/components/ui/typography"
import { useReducedMotion } from "@/hooks/useReducedMotion"
import { sfIcons } from "@/lib/sf-icons"
import { cn } from "@/lib/utils"
import { motion } from "framer-motion"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

// Unified icons via sfIcons for consistent stroke-2
const { checkCircle2: CheckCircle2, user: User, building2: Building2, wallet: Wallet } = sfIcons

type FlowAnimationSectionProps = {
  id?: string
}

// Simple flow data
const flowData = {
  payment: {
    amount: 5000,
    currency: "USD",
    from: "Your client"
  },
  account: {
    type: "ACH",
    routing: "021000021",
    accountMask: "****4567"
  },
  result: {
    amount: 4985,
    token: "USDC",
    status: "Ready to use"
  }
}

type FlowStep = "payment" | "account" | "result"

const STEP_DURATION = 2000 // 2 seconds per step
const PAUSE_DURATION = 1000 // 1 second pause at end
const TOTAL_CYCLE = STEP_DURATION * 3 + PAUSE_DURATION

export function FlowAnimationSection({ id }: FlowAnimationSectionProps) {
  const { t } = useTranslation("marketing")
  const prefersReducedMotion = useReducedMotion()
  const [currentStep, setCurrentStep] = useState<FlowStep>("payment")
  const [stepProgress, setStepProgress] = useState(0)

  // Simple loop animation
  useEffect(() => {
    if (prefersReducedMotion) {
      setCurrentStep("result")
      setStepProgress(100)
      return
    }

    let timeoutId: NodeJS.Timeout
    let intervalId: NodeJS.Timeout

    const animateStep = (step: FlowStep, onComplete: () => void) => {
      setCurrentStep(step)
      setStepProgress(0)

      const startTime = Date.now()
      intervalId = setInterval(() => {
        const elapsed = Date.now() - startTime
        const progress = Math.min((elapsed / STEP_DURATION) * 100, 100)
        setStepProgress(progress)

        if (progress >= 100) {
          clearInterval(intervalId)
          onComplete()
        }
      }, 16)
    }

    const cycle = () => {
      animateStep("payment", () => {
        timeoutId = setTimeout(() => {
          animateStep("account", () => {
            timeoutId = setTimeout(() => {
              animateStep("result", () => {
                timeoutId = setTimeout(() => {
                  cycle()
                }, PAUSE_DURATION)
              })
            }, 50)
          })
        }, 50)
      })
    }

    cycle()

    return () => {
      if (timeoutId) clearTimeout(timeoutId)
      if (intervalId) clearInterval(intervalId)
    }
  }, [prefersReducedMotion])

  // Format number with typewriter effect
  const formatAmount = (amount: number, progress: number) => {
    if (prefersReducedMotion) return amount.toLocaleString()
    const target = Math.floor(amount * (progress / 100))
    return target.toLocaleString()
  }

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.4,
        ease: "easeOut",
      },
    },
  }

  const arrowVariants = {
    hidden: { pathLength: 0, opacity: 0 },
    visible: (step: FlowStep) => ({
      pathLength: currentStep === step ? 1 : 0.3,
      opacity: currentStep === step ? 1 : 0.5,
      transition: {
        duration: 0.6,
        ease: "easeInOut",
      },
    }),
  }

  return (
    <section
      id={id}
      className={cn(
        "relative py-16 sm:py-24 overflow-hidden",
        "bg-slate-50/50 dark:bg-slate-950/50"
      )}
    >
      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="text-center mb-12"
        >
          <Text
            variant="h2"
            className="text-3xl sm:text-4xl font-bold tracking-tight mb-4"
          >
            {t("flowAnimation.headline")}
          </Text>
          <Text variant="lg" color="muted" className="max-w-2xl mx-auto">
            {t("flowAnimation.description")}
          </Text>
        </motion.div>

        {/* Flow Animation */}
        <div className="relative">
          {/* Desktop: Horizontal layout */}
          <div className="hidden md:flex items-center justify-center gap-8 lg:gap-12">
            {/* Step 1: Payment */}
            <FlowStepCard
              step="payment"
              currentStep={currentStep}
              progress={currentStep === "payment" ? stepProgress : currentStep === "account" || currentStep === "result" ? 100 : 0}
              icon={User}
              title={flowData.payment.from}
              subtitle={t("flowAnimation.step1.subtitle")}
              amount={formatAmount(flowData.payment.amount, currentStep === "payment" ? stepProgress : 100)}
              currency={flowData.payment.currency}
              showCheck={currentStep !== "payment" && stepProgress === 100}
              variants={itemVariants}
            />

            {/* Arrow 1 */}
            <motion.div
              className="flex-shrink-0"
              variants={itemVariants}
            >
              <svg
                width="80"
                height="20"
                viewBox="0 0 80 20"
                className="text-slate-300 dark:text-slate-700"
              >
                <motion.path
                  d="M 0 10 L 80 10"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                  variants={arrowVariants}
                  custom="payment"
                />
                <motion.path
                  d="M 70 5 L 80 10 L 70 15"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                  variants={arrowVariants}
                  custom="payment"
                />
              </svg>
            </motion.div>

            {/* Step 2: Account */}
            <FlowStepCard
              step="account"
              currentStep={currentStep}
              progress={currentStep === "account" ? stepProgress : currentStep === "result" ? 100 : 0}
              icon={Building2}
              title={t("flowAnimation.step2.title")}
              subtitle={`${flowData.account.type}: ${flowData.account.routing}`}
              accountMask={flowData.account.accountMask}
              showCheck={currentStep === "result" && stepProgress === 100}
              variants={itemVariants}
            />

            {/* Arrow 2 */}
            <motion.div
              className="flex-shrink-0"
              variants={itemVariants}
            >
              <svg
                width="80"
                height="20"
                viewBox="0 0 80 20"
                className="text-slate-300 dark:text-slate-700"
              >
                <motion.path
                  d="M 0 10 L 80 10"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                  variants={arrowVariants}
                  custom="account"
                />
                <motion.path
                  d="M 70 5 L 80 10 L 70 15"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                  variants={arrowVariants}
                  custom="account"
                />
              </svg>
            </motion.div>

            {/* Step 3: Result */}
            <FlowStepCard
              step="result"
              currentStep={currentStep}
              progress={currentStep === "result" ? stepProgress : 0}
              icon={Wallet}
              title={t("flowAnimation.step3.title")}
              subtitle={flowData.result.status}
              amount={formatAmount(flowData.result.amount, currentStep === "result" ? stepProgress : 0)}
              token={flowData.result.token}
              showCheck={currentStep === "result" && stepProgress === 100}
              variants={itemVariants}
            />
          </div>

          {/* Mobile: Vertical layout */}
          <div className="md:hidden flex flex-col items-center gap-6">
            <FlowStepCard
              step="payment"
              currentStep={currentStep}
              progress={currentStep === "payment" ? stepProgress : currentStep === "account" || currentStep === "result" ? 100 : 0}
              icon={User}
              title={flowData.payment.from}
              subtitle={t("flowAnimation.step1.subtitle")}
              amount={formatAmount(flowData.payment.amount, currentStep === "payment" ? stepProgress : 100)}
              currency={flowData.payment.currency}
              showCheck={currentStep !== "payment" && stepProgress === 100}
              variants={itemVariants}
            />

            <motion.div
              className="flex-shrink-0"
              variants={itemVariants}
            >
              <svg
                width="20"
                height="60"
                viewBox="0 0 20 60"
                className="text-slate-300 dark:text-slate-700"
              >
                <motion.path
                  d="M 10 0 L 10 60"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                  variants={arrowVariants}
                  custom="payment"
                />
                <motion.path
                  d="M 5 50 L 10 60 L 15 50"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                  variants={arrowVariants}
                  custom="payment"
                />
              </svg>
            </motion.div>

            <FlowStepCard
              step="account"
              currentStep={currentStep}
              progress={currentStep === "account" ? stepProgress : currentStep === "result" ? 100 : 0}
              icon={Building2}
              title={t("flowAnimation.step2.title")}
              subtitle={`${flowData.account.type}: ${flowData.account.routing}`}
              accountMask={flowData.account.accountMask}
              showCheck={currentStep === "result" && stepProgress === 100}
              variants={itemVariants}
            />

            <motion.div
              className="flex-shrink-0"
              variants={itemVariants}
            >
              <svg
                width="20"
                height="60"
                viewBox="0 0 20 60"
                className="text-slate-300 dark:text-slate-700"
              >
                <motion.path
                  d="M 10 0 L 10 60"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                  variants={arrowVariants}
                  custom="account"
                />
                <motion.path
                  d="M 5 50 L 10 60 L 15 50"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                  variants={arrowVariants}
                  custom="account"
                />
              </svg>
            </motion.div>

            <FlowStepCard
              step="result"
              currentStep={currentStep}
              progress={currentStep === "result" ? stepProgress : 0}
              icon={Wallet}
              title={t("flowAnimation.step3.title")}
              subtitle={flowData.result.status}
              amount={formatAmount(flowData.result.amount, currentStep === "result" ? stepProgress : 0)}
              token={flowData.result.token}
              showCheck={currentStep === "result" && stepProgress === 100}
              variants={itemVariants}
            />
          </div>
        </div>
      </div>
    </section>
  )
}

type FlowStepCardProps = {
  step: FlowStep
  currentStep: FlowStep
  progress: number
  icon: React.ComponentType<{ className?: string }>
  title: string
  subtitle: string
  amount?: string
  currency?: string
  token?: string
  accountMask?: string
  showCheck?: boolean
  variants: any
}

function FlowStepCard({
  step,
  currentStep,
  progress,
  icon: Icon,
  title,
  subtitle,
  amount,
  currency,
  token,
  accountMask,
  showCheck,
  variants,
}: FlowStepCardProps) {
  const isActive = currentStep === step
  const isCompleted = (step === "payment" && (currentStep === "account" || currentStep === "result")) ||
    (step === "account" && currentStep === "result")

  return (
    <motion.div
      variants={variants}
      className={cn(
        "relative w-full max-w-sm",
        "bg-white dark:bg-slate-900",
        "border border-slate-200 dark:border-slate-800",
        "rounded-lg p-6",
        "transition-all duration-300",
        isActive && "ring-2 ring-blue-500 dark:ring-blue-400 shadow-lg",
        isCompleted && "opacity-100"
      )}
    >
      {/* Icon */}
      <div className="flex items-center justify-center mb-4">
        <div
          className={cn(
            "p-3 rounded-full",
            isActive
              ? "bg-blue-100 dark:bg-blue-900/30"
              : isCompleted
                ? "bg-green-100 dark:bg-green-900/30"
                : "bg-slate-100 dark:bg-slate-800"
          )}
        >
          {showCheck ? (
            <CheckCircle2
              className={cn(
                "w-6 h-6",
                "text-green-600 dark:text-green-400"
              )}
            />
          ) : (
            <Icon
              className={cn(
                "w-6 h-6",
                isActive
                  ? "text-blue-600 dark:text-blue-400"
                  : "text-slate-600 dark:text-slate-400"
              )}
            />
          )}
        </div>
      </div>

      {/* Title */}
      <Text variant="lg" className="font-semibold mb-2 text-center">
        {title}
      </Text>

      {/* Subtitle */}
      <Text variant="sm" color="muted" className="text-center mb-4">
        {subtitle}
      </Text>

      {/* Amount / Account Details */}
      {amount && (
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: isActive ? 1 : 0.7, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Text
            variant="h3"
            className={cn(
              "font-bold",
              isActive ? "text-blue-600 dark:text-blue-400" : "text-slate-900 dark:text-slate-100"
            )}
          >
            {currency && `$${amount}`}
            {token && `${amount} ${token}`}
          </Text>
        </motion.div>
      )}

      {accountMask && (
        <Text variant="sm" color="muted" className="text-center mt-2">
          {accountMask}
        </Text>
      )}

      {/* Progress indicator (optional, subtle) */}
      {isActive && (
        <motion.div
          className="absolute bottom-0 left-0 right-0 h-1 bg-blue-500 dark:bg-blue-400 rounded-b-lg"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: progress / 100 }}
          transition={{ duration: 0.1, ease: "linear" }}
        />
      )}
    </motion.div>
  )
}
